import * as React from 'react'
import clsx from 'clsx'
import { useGridApiContext } from '../../hooks/utils/useGridApiContext'
import { useGridSelector } from '../../hooks/utils/useGridSelector'
import { GridGenericColumnHeaderItem } from './GridGenericColumnHeaderItem'
import { ReactTableHeader } from '../../models/gridReactTable'
import { gridHeaderGroupDependentsSelector } from '../../hooks/features/headerGroupDependents'
import { useGridRootProps } from '../../hooks/utils/useGridRootProps'
import { GridColumnGroupHeaderEventLookup } from '../../models/events'
import { isReactComponent } from '@applift/factor'
import { GridColumnGroupHeaderParams } from '../../models/params'
import styles from '../../style'

export type Renderable<TProps> = React.ComponentType<TProps>

export function render<TProps extends Object>(
  Comp: Renderable<TProps>,
  props: TProps
): React.ReactNode | JSX.Element {
  return <Comp {...props} />
}

export type HeaderGroupSide = 'left' | 'center' | 'right'

interface GridColumnGroupHeaderProps extends ReactTableHeader {
  pinnedSide: HeaderGroupSide
  colIndex: number
  cellHeight: number
  isLastColumn: boolean
  extendRowFullWidth: boolean
  showColumnRightBorder: boolean
  maxDepth: number
}

export function GridColumnGroupHeader(props: GridColumnGroupHeaderProps) {
  const {
    pinnedSide,
    colIndex,
    cellHeight,
    isLastColumn,
    extendRowFullWidth,
    showColumnRightBorder,
    id,
    getContext,
    getSize,
    column,
    depth,
    maxDepth,
  } = props

  const headerCellRef = React.useRef<HTMLDivElement>(null)
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const fields = useGridSelector(apiRef, gridHeaderGroupDependentsSelector)[
    pinnedSide
  ]![id]!

  const { hasScrollX, hasScrollY } = apiRef.current.getRootDimensions() ?? {
    hasScrollX: false,
    hasScrollY: false,
  }
  const removeLastBorderRight = isLastColumn && hasScrollX && !hasScrollY
  const showRightBorder = !isLastColumn
    ? showColumnRightBorder
    : !removeLastBorderRight && !extendRowFullWidth

  const header = column.columnDef.header
  const width = getSize()
  const headerAlign = column.columnDef.meta?.headerAlign ?? 'left'

  let label: any = column.id
  let headerComponent: React.ReactNode
  if (isReactComponent(header)) {
    headerComponent = render(header, {
      ...getContext(),
      others: {
        isLastColumn,
        removeLastBorderRight,
        showRightBorder,
        fields,
      },
    })
  } else {
    label = header
  }

  const renderParams: GridColumnGroupHeaderParams = React.useMemo(
    () => ({
      id,
      depth,
      maxDepth,
      isLastColumn,
      fields,
      colIndex,
    }),
    [id, depth, maxDepth, isLastColumn, fields, colIndex]
  )

  const publish = React.useCallback(
    (eventName: keyof GridColumnGroupHeaderEventLookup) =>
      (event: React.SyntheticEvent) => {
        // Ignore portal
        // See https://github.com/mui/mui-x/issues/1721
        if (!event.currentTarget.contains(event.target as Element)) {
          return
        }
        apiRef.current.publishEvent(eventName, renderParams, event as any)
      },
    // For now this is stupid, because renderParams change all the time.
    // Need to move it's computation in the api, such that for a given depth+columnField, I can get the group parameters
    [apiRef, renderParams]
  )

  const mouseEventsHandlers = React.useMemo(
    () => ({
      onKeyDown: publish('columnGroupHeaderKeyDown'),
      onFocus: publish('columnGroupHeaderFocus'),
      onBlur: publish('columnGroupHeaderBlur'),
    }),
    [publish]
  )

  const classes = React.useMemo(() => {
    return {
      root: clsx(
        [styles.columnHeader, rootProps.classes?.columnHeader],
        headerAlign === 'left' && [
          styles['columnHeader--alignLeft'],
          rootProps.classes?.['columnHeader--alignLeft'],
        ],
        headerAlign === 'center' && [
          styles['columnHeader--alignCenter'],
          rootProps.classes?.['columnHeader--alignCenter'],
        ],
        headerAlign === 'right' && [
          styles['columnHeader--alignRight'],
          rootProps.classes?.['columnHeader--alignRight'],
        ],
        showRightBorder && [styles.withBorder, rootProps.classes?.withBorder]
      ),
      draggableContainer: clsx([
        styles.columnHeaderDraggableContainer,
        rootProps.classes?.columnHeaderDraggableContainer,
      ]),
      titleContainer: clsx([
        styles.columnHeaderTitleContainer,
        rootProps.classes?.columnHeaderTitleContainer,
      ]),
      titleContainerContent: clsx([
        styles.columnHeaderTitleContainerContent,
        rootProps.classes?.columnHeaderTitleContainerContent,
      ]),
    }
  }, [headerAlign, showRightBorder, rootProps])

  return (
    <GridGenericColumnHeaderItem
      ref={headerCellRef}
      classes={classes}
      colIndex={colIndex}
      height={cellHeight}
      isResizing={false}
      sortDirection={null}
      isDraggable={false}
      headerComponent={headerComponent}
      width={width}
      columnTitleIconButtons={null}
      resizable={false}
      tabIndex={-1}
      hasFocus={false}
      label={label}
      aria-colspan={fields?.length}
      // The fields are wrapped between |-...-| to avoid confusion between fields "id" and "id2" when using selector data-fields~=
      data-fields={`|-${fields?.join('-|-')}-|`}
      //There will be no seperator control for header groups
      disableHeaderSeparator
      {...mouseEventsHandlers}
    />
  )
}
